import { locale, t } from './i18n.js';

/**
 * Les formats qui dépendent de la langue — et **rien d'autre** dans ce fichier.
 *
 * Ils lisent `locale.value`, donc un changement de langue les refait passer sans
 * qu'aucun écran n'ait à le savoir.
 */

const UNITS_FR = ['o', 'Kio', 'Mio', 'Gio', 'Tio'];
const UNITS_EN = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];

/** Une taille lisible. **L'unité est portée par la valeur**, jamais sous-entendue. */
export function formatSize(bytes: number): string {
  const units = locale.value === 'fr' ? UNITS_FR : UNITS_EN;
  let value = Math.max(0, bytes);
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const digits = unit === 0 ? 0 : value < 10 ? 1 : 0;
  return `${value.toFixed(digits)} ${units[unit]}`;
}

const tag = (): string => (locale.value === 'fr' ? 'fr-FR' : 'en-GB');

/**
 * Une date **de document**, jamais un filigrane de synchronisation (règle E7) :
 * `UpdatedAt` avance sur une réaction, et l'afficher comme « modifié le »
 * ferait mentir l'écran. C'est à l'appelant de choisir la bonne source ; cette
 * fonction ne fait que mettre en forme.
 */
export function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(tag());
}

export function formatDateTime(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(tag());
}

/**
 * Un en-tête de mois — `"2026-08"` devient « août 2026 ».
 *
 * Le jour n'est pas dans la clé, et la fabriquer avec `new Date('2026-08')`
 * l'interpréterait en **UTC** : à Paris en hiver, le 1ᵉʳ janvier reculerait en
 * décembre, et un groupe entier changerait de titre. La date est donc construite
 * en heure locale, à partir des deux nombres.
 */
export function formatMonth(key: string): string {
  const [year, month] = key.split('-').map(Number);
  if (!year || !month) return key;
  const label = new Date(year, month - 1, 1).toLocaleDateString(tag(), {
    month: 'long',
    year: 'numeric',
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * **Une position, écrite comme on la recopie** *(R4, 2026-09-12)*.
 *
 * Point décimal et virgule entre les deux nombres, même en français : c'est la
 * forme qu'attendent toutes les cartes, et c'est à ça qu'elle sert — on la colle
 * ailleurs. L'écrire à la française (`48,85822`) rendrait la paire ambiguë, la
 * virgule y servant deux fois.
 *
 * Cinq décimales, soit un peu plus d'un mètre. C'est en dessous de ce que l'EXIF
 * d'un téléphone garantit, donc rien d'utile n'est perdu, et ça évite d'afficher
 * les dix-sept chiffres d'un flottant.
 */
export function formatCoordinates(lat: number, lon: number): string {
  return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
}

/** Les initiales d'un alias, pour l'avatar tant qu'il n'y a pas d'image (§7 de `api.md`). */
export function initials(alias: string | undefined): string {
  const parts = (alias ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  const first = [...parts[0]!][0] ?? '';
  const last = parts.length > 1 ? ([...parts[parts.length - 1]!][0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * **L'état d'une entité, et la cause de son échec** — deux libellés que trois
 * écrans se partagent, donc posés ici et non dans l'un d'eux.
 *
 * Ils vivaient dans `ui/space.tsx`, d'où `ui/gallery.tsx` a dû les importer le
 * 2026-09-12 — créant un **cycle** entre deux modules d'écran, que les
 * déclarations hoistées auraient masqué jusqu'au jour où l'une d'elles cesse d'en
 * être une. Un libellé qui dépend de la langue et de rien d'autre appartient au
 * module des formats.
 */
export function stateLabel(state: string): string {
  return (
    {
      PENDING_UPLOAD: t('entity.pendingUpload'),
      PROCESSING: t('entity.processing'),
      FAILED: t('entity.failed'),
    }[state] ?? state
  );
}

/**
 * **Le motif d'un échec, traduit — et jamais avalé** *(2026-09-12)*.
 *
 * `FailReason` est servi par l'API depuis l'étape 1 et aucun écran ne le lisait.
 * Les 42 photos perdues en production l'ont montré au prix le plus élevé : « échec
 * du traitement » couvrait cinq causes, si bien que le retour d'usage ne pouvait
 * rien apporter de plus que le mot « échec ».
 *
 * Un motif que ce client ne connaît pas est rendu **brut** plutôt que masqué. Un
 * code se recopie dans un rapport ; un libellé générique ferait exactement
 * regresser vers ce qu'on corrige ici. C'est la même règle que pour
 * `error.generic`, et le même piège que N6 avait déjà payé.
 */
export function failReasonLabel(reason: string | undefined): string | undefined {
  if (!reason) return undefined;
  const key = `fail.${reason}`;
  const named = t(key);
  return named === key ? t('fail.unknown', { reason }) : named;
}

/** L'état **et** sa cause quand il y en a une, pour une infobulle ou une ligne de méta. */
export function stateWithReason(state: string, reason: string | undefined): string {
  const label = stateLabel(state);
  const why = failReasonLabel(reason);
  return why ? t('entity.failedBecause', { label, reason: why }) : label;
}
